import type { WizardComponentAdapterProps } from '@lemonade-hq/chat-wizard-client-base';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type { UniclientCoInsurance } from '@lemonade-hq/uniclient-chat';
import { Flex, IconButton, Layout, spacing, Text } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InterpolableText } from './InterpolableText';
import * as styles from './WizardFooter.css';

const ANIMATION_DURATION: Milliseconds = 0.3;
const ANIMATION_DELAY: Milliseconds = 0.2;
const FOOTER_BASE_HEIGHT = '44px';

interface WizardFooterProps {
  readonly coInsurance?: UniclientCoInsurance;
  readonly onAction?: WizardComponentAdapterProps['onAction'];
  readonly expanded?: boolean;
  readonly hidden?: boolean;
}

const footerVariants = {
  hidden: {
    translateY: '100%',
  },
  visible: {
    translateY: 0,
    height: 'auto',
  },
};

const contentVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: {
      height: { duration: ANIMATION_DURATION, delay: ANIMATION_DELAY },
    },
  },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: {
      opacity: { duration: ANIMATION_DURATION, delay: ANIMATION_DELAY },
    },
  },
};

const iconVariants = {
  collapsed: {
    rotate: 0,
  },
  expanded: {
    rotate: 180,
  },
};

export const WizardFooter: React.FC<WizardFooterProps> = ({ coInsurance, onAction, expanded, hidden }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(expanded ?? false);
  const footerRef = useRef<HTMLDivElement>(null);
  const hasBeenToggled = useRef<boolean>(false);

  const hasFooter = coInsurance?.logo != null && coInsurance.position === 'bottom';
  const hasLinks = coInsurance?.links != null && coInsurance.links.length > 0;

  const toggleExpand = useCallback((): void => {
    setIsExpanded(prev => !prev);
    hasBeenToggled.current = true;
  }, []);

  useEffect(() => {
    if (expanded) {
      setIsExpanded(true);
    }
  }, [expanded]);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent): void => {
      if (footerRef.current && !footerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  if (!hasFooter) {
    return null;
  }

  return (
    <AnimatePresence>
      {!hidden && (
        <Flex
          alignItems="center"
          animate="visible"
          as={motion.div}
          backgroundColor="$backgroundSecondary"
          className={styles.footer}
          exit="hidden"
          flexDirection="column"
          initial="hidden"
          justifyContent="flex-end"
          ref={footerRef}
          transition={{ duration: ANIMATION_DURATION, ease: 'easeInOut' }}
          variants={footerVariants}
          width="100%"
        >
          {hasLinks && (
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  animate="visible"
                  exit="hidden"
                  initial={expanded && !hasBeenToggled.current ? 'visible' : 'hidden'}
                  key="coInsuranceLinksContent"
                  style={{ overflow: 'hidden' }}
                  variants={contentVariants}
                >
                  <Flex
                    alignItems="center"
                    flexWrap="wrap"
                    gap={spacing.s16}
                    justifyContent="center"
                    padding={spacing.s16}
                    width="100%"
                  >
                    {coInsurance.links.map(link => (
                      <Text color="$textDark" key={typeof link === 'string' ? link : link.text} type="bodyMedium">
                        <InterpolableText onAction={onAction} text={link} />
                      </Text>
                    ))}
                  </Flex>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          <Flex
            alignItems="center"
            gap={spacing.s08}
            justifyContent="center"
            minHeight={FOOTER_BASE_HEIGHT}
            paddingBlockEnd={styles.FOOTER_PADDING_BLOCK_END}
            paddingBlockStart={styles.FOOTER_PADDING}
            width="100%"
          >
            {coInsurance.text != null && (
              <Text color="$textDark" type="bodySmall">
                {coInsurance.text}
              </Text>
            )}
            <Layout alt={coInsurance.alt} as="img" height={styles.FOOTER_LOGO_HEIGHT} src={coInsurance.logo} />

            {hasLinks && (
              <motion.div animate={isExpanded ? 'expanded' : 'collapsed'} initial="collapsed" variants={iconVariants}>
                <IconButton background="transparent" icon="dropdown" onClick={toggleExpand} size="sm" />
              </motion.div>
            )}
          </Flex>
        </Flex>
      )}
    </AnimatePresence>
  );
};
